import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { checkout } from "@/lib/api";
import { Overline, PrimaryButton } from "@/components/site/Primitives";
import { CheckoutDialog } from "@/components/site/CheckoutDialog";
import { AuthForm } from "@/components/site/AuthForm";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const PERKS = [
  "Every weekly Journal letter, in full",
  "A fresh recipe from Agnes every single day",
  "AgnesAI — your daily cottage companion, included free",
  "The complete members' recipe archive",
  "Seasonal remedies, rituals and homemaking wisdom",
  "New members-only content, always growing",
];

export default function Membership() {
  const { user, ready } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const doCheckout = async (name, email) => {
    setLoading(true);
    try {
      const res = await checkout({ lookup_key: "cottage-membership", origin_url: window.location.origin, name, email });
      window.location.href = res.checkout_url;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open checkout. Please try again."); setLoading(false); }
  };

  return (
    <div data-testid="membership-page">
      <section className="max-w-3xl mx-auto px-6 pt-24 pb-10 text-center">
        <Overline>The Cottage Circle</Overline>
        <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light mt-5 mb-6 leading-none">Become a Member</h1>
        <p className="font-body text-brand-body text-lg leading-relaxed">
          Pull up a chair at Agnes's table. For a little each month, receive a daily recipe and every letter
          from the cottage — a gentle companion for a slower, warmer life.
        </p>
      </section>

      <section className="max-w-4xl mx-auto px-6 pb-24 grid md:grid-cols-2 gap-12 items-center">
        <div className="bg-brand-warm-white border border-brand-linen p-10">
          <span className="overline">Membership</span>
          <div className="flex items-baseline gap-2 mt-3 mb-6">
            <span className="font-serif text-6xl text-brand-charcoal">$9</span>
            <span className="font-body text-brand-muted">/ month</span>
          </div>
          <ul className="space-y-4 mb-8">
            {PERKS.map((p, i) => (
              <li key={i} className="flex gap-3 items-start">
                <Check size={18} className="text-brand-sage mt-1 shrink-0" />
                <span className="font-body text-brand-body">{p}</span>
              </li>
            ))}
          </ul>
          {ready && user && user.is_subscribed ? (
            <div className="text-center">
              <p className="font-serif text-2xl text-brand-olive mb-4">You're a member — thank you.</p>
              <Link to="/daily-recipe" className="link-underline font-sans text-xs uppercase tracking-[0.16em] text-brand-copper">Go to today's recipe</Link>
            </div>
          ) : ready && user ? (
            <PrimaryButton onClick={() => setCheckoutOpen(true)} testid="subscribe-btn" disabled={loading}>
              {loading ? "Opening checkout…" : "Join for $9/month"}
            </PrimaryButton>
          ) : null}
          <p className="font-sans text-[0.68rem] uppercase tracking-[0.14em] text-brand-muted mt-5">Cancel anytime · Secure checkout</p>
        </div>

        <div>
          {ready && !user ? (
            <AuthForm intro={<p className="font-body text-brand-body mb-6 text-center">Create your free account to become a member.</p>} />
          ) : (
            <div className="text-center px-6">
              <p className="font-serif text-3xl leading-snug text-brand-charcoal italic">"From my cottage to yours — a little wisdom, every day."</p>
              <p className="overline mt-6 block">Agnes Whitmore</p>
            </div>
          )}
        </div>
      </section>
      <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} onConfirm={doCheckout} submitting={loading} title="Join the Cottage Circle" />
    </div>
  );
}
